import axios from "axios";

const url = "https://cognito-idp.us-west-1.amazonaws.com/";

const normalizeEmail = (email) => (email || "").trim().toLowerCase();

export const login = async (email, password) => {
  const headers = {
    "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
    "Content-Type": "application/x-amz-json-1.1",
  };

  const body = {
    AuthParameters: {
      USERNAME: normalizeEmail(email),
      PASSWORD: password,
    },
    AuthFlow: "USER_PASSWORD_AUTH",
    ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
  };

  try {
    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Login success");
      localStorage.setItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`,
        result.data.AuthenticationResult.AccessToken
      );
      localStorage.setItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`,
        "true"
      );

      return { success: true };
    }
  } catch (e) {
    let error = `Error logging in: ${e.message}`;
    if (e?.response?.data?.message) {
      error = e.response.data.message;
    }
    localStorage.removeItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    localStorage.removeItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`
    );

    console.error(error);
    console.log(e);
    return { success: false, error };
  }
};

export const signup = async (email, password, phone, orgId, smsChecked, clientMetadata = null) => {
  const headers = {
    "X-Amz-Target": "AWSCognitoIdentityProviderService.SignUp",
    "Content-Type": "application/x-amz-json-1.1",
  };

  let receiveSms = 'false';
  if (smsChecked) {
    receiveSms = 'true'
  }

  const body = {
    Username: normalizeEmail(email),
    Password: password,
    ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    UserAttributes: [
      {
        Name: "phone_number",
        Value: phone,
      },
      {
        Name: "custom:orgId",
        Value: orgId
      },
      {
        Name: 'custom:receiveSms',
        Value: receiveSms
      }
    ],
  };

  // ClientMetadata is forwarded (not stored) to the Cognito CustomMessage
  // Lambda so it can build a magic-link confirmation email. Cognito requires
  // all values to be strings.
  if (clientMetadata) {
    body.ClientMetadata = clientMetadata;
  }

  try {
    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Signup success");
      return { success: true };
    }
  } catch (e) {
    let error = `Error signing up: ${e?.response?.data?.message}`;
    console.error(error);
    // alert(error);
    return { success: false, error };
  }
};

export const confirm = async (email, code) => {
  let error;
  try {
    const headers = {
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ConfirmSignUp",
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      Username: normalizeEmail(email),
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
      ConfirmationCode: (code || "").trim(),
    };

    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Confirm success");
      return { success: true };
    }
  } catch (e) {
    if (e?.response?.data?.__type === "CodeMismatchException") {
      return { success: false, error: "Invalid code. Please try again" };
    }
    error = `Error signing confirming user. Error: ${e.message}`;
    console.error(error);
    return { success: false, error };
  }
};

export const updateEmail = async (email) => {
  let error;
  try {
    const token = localStorage.getItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    if (!token) {
      localStorage.removeItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`
      );
      return { success: false };
    }

    const headers = {
      "X-Amz-Target": `AWSCognitoIdentityProviderService.UpdateUserAttributes`,
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      AccessToken: token,
      UserAttributes: [
        {
          Name: "email",
          Value: normalizeEmail(email),
        },
      ],
    };
    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Email request success");
      return { success: true };
    }
  } catch (e) {
    error = `Error updating email. Error: ${e.message}`;
    console.error(error);
    return { success: false, error };
  }
};

export const updatePhone = async (phone) => {
  let error;
  try {
    const token = localStorage.getItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    if (!token) {
      localStorage.removeItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`
      );
      return { success: false };
    }

    const headers = {
      "X-Amz-Target": `AWSCognitoIdentityProviderService.UpdateUserAttributes`,
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      AccessToken: token,
      UserAttributes: [
        {
          Name: "phone_number",
          Value: phone,
        },
      ],
    };
    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Phone update request success");
      return { success: true };
    }
  } catch (e) {
    error = `Error updating phone. Error: ${e.message}`;
    console.error(error);
    return { success: false, error };
  }
};

export const validateEmailCode = async (code, email) => {
  let error;
  try {
    const token = localStorage.getItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    if (!token) {
      localStorage.removeItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`
      );
      return { success: false };
    }

    const headers = {
      "X-Amz-Target": `AWSCognitoIdentityProviderService.VerifyUserAttribute`,
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      AccessToken: token,
      AttributeName: "email",
      Code: code,
    };
    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Code confirmed");
      return { success: true };
    } else {
      return {success: false}
    }
  } catch (e) {
    error = `Error updating email. Error: ${e.message}`;
    console.error(error);
    return { success: false, error };
  }
};

export const forgot = async (email) => {
  let error;

  try {
    const fBody = { email };
    const fResult = await axios.post(`${process.env.REACT_APP_TNFT_SERVER}/emailvalidator/forgot`, fBody)
    if (!fResult.data?.success) {
      return fResult.data
    }
  } catch (e) {
    console.log(e.message)
    return {success: false, error: e?.response?.data?.error}
  }



  try {
    const headers = {
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ForgotPassword",
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      Username: normalizeEmail(email),
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
    };

    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Forgot success");
      return { success: true };
    }
  } catch (e) {

    error = e?.response?.data?.message;
    console.error(error);
    return { success: false, error };
  }
};

export const confirmForgot = async (email, password, code) => {
  let error;
  try {
    const headers = {
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ConfirmForgotPassword",
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      Username: normalizeEmail(email),
      ConfirmationCode: (code || "").trim(),
      Password: password,
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
    };

    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Confirm forgot success");
      return { success: true };
    }
  } catch (e) {
    if (e?.response?.data?.__type == 'CodeMismatchException') {
      error = "Invalid code. Please try again."
    } else {
      error = `Error resetting password.${e?.response?.data?.message}`;
    }
    console.error(error);
    return { success: false, error };
  }
};

export const resendCode = async (email, clientMetadata = null) => {
  let error;
  try {
    const headers = {
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ResendConfirmationCode",
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      Username: normalizeEmail(email),
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    };

    // Re-pass ClientMetadata so the resent email keeps its magic link.
    if (clientMetadata) {
      body.ClientMetadata = clientMetadata;
    }

    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Confirmation code resent successfully");
      return { success: true };
    }
  } catch (e) {
    error = e?.response?.data?.message || `Error resending confirmation code: ${e.message}`;
    console.error(error);
    alert(error); // Optionally notify the user
    return { success: false, error };
  }
};

export const validateEmail = async (email) => {

  try { 

    const body = {email}
    const headers = {'Content-Type': "application/json"}

    const result = await axios.post(
      `${process.env.REACT_APP_TNFT_SERVER}/emailvalidator`,
      body,
      { headers }
    )


    return result.data;
  
  } catch (e) {
    var error = (`Error validating email. Error: ${e.message}`);
    console.error(error);
    return {success: true}
  }
  


}
